/**
 * Leave Accrual Engine — Single Source of Truth
 *
 * This module is the ONLY place that computes leave entitlements for EL, CL, and SL.
 * Every service, page, report, and chart must import from here. No other file may
 * duplicate or approximate this logic.
 *
 * ## Business Rule (Two Stages)
 *
 * ### Stage 1 — Earned Months (the EL value)
 *
 * "Earned Months" is NOT leave days. It is a count of months the employee has earned
 * in the given calendar year based on their Date of Joining (DOJ).
 *
 *   - Joining month: DOJ day ≤ 15 → 1 full month credit; DOJ day > 15 → 0.5 credit.
 *   - Every subsequent month through December → 1 credit each.
 *   - The half-month rule applies ONLY in the joining year.
 *   - In any year after the joining year, the employee earns 12 full months.
 *   - Year before DOJ → 0. Future year (not yet started) → 0.
 *   - If the employee has exited, accrual stops at the exit month.
 *
 * ### Stage 2 — Derived Leave (EL, CL and SL)
 *
 * Each code accrues at a RATE of so many days per earned month:
 *
 *   EL = earnedMonths × rate.el          keeps its half month, never rounded
 *   CL = Math.round(earnedMonths × rate.cl)
 *   SL = Math.round(earnedMonths × rate.sl)
 *
 * Where the rates come from, in order — both configuration, neither of them here:
 *
 *   1. The Leave Policy governing the employee, matched on their Employment Type. Its
 *      allocation for each code, read as a MONTHLY or a YEARLY figure according to the
 *      policy's own accrual frequency, and 0 for a code it does not allocate.
 *   2. Otherwise the Leave Types list, whose grantedPerYear is an annual figure.
 *
 * Both are edited in Leave Roster > Leave Policies. See LeavePolicyService.ratesForEmployee.
 *
 * This module held the caps itself — Intern meant EL 0, CL 0 and SL at 6/year, Full Time
 * meant 12/10/10 — and nothing could change them. That is why an "Intern Policy" granting
 * Casual Leave 3 sat on the Leave Policies screen marked ACTIVE and did nothing whatsoever,
 * with no hint on the screen that its number was being ignored. A leave entitlement is a
 * business decision, so it is data now, and this file computes rather than decides.
 *
 * Employment Type still matters: it is how a policy finds the employee. That field lives in
 * the DynamoDB profile store, not Cognito — the pool schema cannot safely gain a custom
 * attribute post-creation (see SimpleUser.employmentType).
 *
 * Standard mathematical rounding (0.5 rounds up). CL and SL are whole integers; EL keeps a
 * half, because earned months are themselves .5 in a year joined after the 15th.
 *
 * ### Other Leave Types
 *
 * ML (Maternity) and CO (Comp-Off) are NOT computed by this engine. They remain
 * policy-driven and are handled by LeaveBalanceService using their respective
 * grantedPerYear values, for every employment type alike.
 */

/**
 * Stage 1: Calculate Earned Months for a given calendar year.
 *
 * Returns a decimal — for example 4.5 when the employee joined after the 15th
 * and has worked 4 subsequent full months, or 12 for a full year.
 *
 * @param dateOfJoining  ISO date string (e.g. "2026-08-05")
 * @param year           The calendar year to compute for (e.g. 2026)
 * @param dateOfExit     Optional ISO date string; accrual stops at this month
 */
export function computeEarnedMonths(
  dateOfJoining: string,
  year: number,
  dateOfExit?: string
): number {
  const doj = new Date(dateOfJoining);
  if (isNaN(doj.getTime())) return 0;

  const dojYear = doj.getFullYear();
  const dojMonth = doj.getMonth(); // 0-based
  const dojDay = doj.getDate();

  const now = new Date();
  const currentYear = now.getFullYear();

  // Future year relative to today: not yet started.
  if (year > currentYear) return 0;
  // Employee has not joined yet as of the requested year.
  if (year < dojYear) return 0;

  /*
   * Upper bound for accrual is December — the entitlement is for the whole leave year, not
   * for the part of it that has already elapsed.
   *
   * This used to be `year === currentYear ? now.getMonth() : 11`, i.e. accrue-to-today, and
   * that was wrong in two ways. It contradicted the specified rule (join on 18 March and the
   * year's entitlement runs March→December, giving 9.5 — not 5.5 because today happens to be
   * August). And it contradicted the very next branch, which has always returned a full 12
   * for any year after the joining year regardless of the date: on 1 January someone who
   * joined last year was granted all 12 months, while someone who joined in March of the
   * current year was granted only the months already gone. Same entitlement concept, two
   * different models, depending on which branch you landed in.
   *
   * Consequence worth knowing: an employee's entitlement no longer grows month by month
   * through their joining year — it is the full remainder of the year from the day they join.
   */
  let lastMonthIndex = 11;

  // Cap at exit month if the employee has left or has a scheduled exit date. This is the one
  // thing that still shortens a year, because it genuinely ends the entitlement.
  const exit = dateOfExit ? new Date(dateOfExit) : null;
  if (exit && !isNaN(exit.getTime())) {
    const exitYear = exit.getFullYear();
    if (year > exitYear) return 0; // left before this year began
    if (year === exitYear) {
      lastMonthIndex = Math.min(lastMonthIndex, exit.getMonth());
    }
  }

  if (year === dojYear) {
    // Joining year: half a month if they joined after the 15th, then one per whole month
    // from the month after joining through lastMonthIndex (December, or the exit month).
    if (dojMonth > lastMonthIndex) return 0; // exits before they ever started accruing
    let monthCredits = dojDay <= 15 ? 1 : 0.5;
    monthCredits += Math.max(0, lastMonthIndex - dojMonth);
    return Math.min(12, monthCredits);
  } else {
    // Subsequent years after DOJ: full 12-month entitlement, or up to the exit month.
    if (exit && !isNaN(exit.getTime()) && year === exit.getFullYear()) {
      return Math.min(12, lastMonthIndex + 1);
    }
    return 12;
  }
}

/**
 * Stage 2: Derive CL or SL from Earned Months.
 *
 * Formula: Math.round((earnedMonths × annualCap) / 12)
 *
 * Uses standard mathematical rounding:
 *   0.49 → 0, 0.50 → 1, 3.49 → 3, 3.50 → 4, 4.17 → 4, 3.75 → 4
 *
 * Always returns a whole integer (never fractional).
 *
 * `annualCap` is required and has no default. It used to default to 10 — the Full Time
 * entitlement — which is exactly the kind of number that belongs in configuration rather
 * than in a function signature, and a caller that forgot to pass one silently got the
 * full-time schedule.
 */
export function computeDerivedLeave(earnedMonths: number, annualCap: number): number {
  return Math.round((earnedMonths * annualCap) / 12);
}

/**
 * Earned months and the three grants, in one call.
 *
 * `rates` is required. There is no employment-type argument and no fallback schedule: what
 * an employee accrues is configuration — the Leave Policy governing them, or failing that
 * the Leave Types list — and this module holds no opinion about the numbers. See
 * LeavePolicyService.ratesForEmployee, where those two sources are resolved.
 *
 * ML and CO are NOT included — they are accrued from their own allocation.
 */
export function computeAllLeaveGrants(
  dateOfJoining: string,
  year: number,
  dateOfExit: string | undefined,
  rates: LeaveAccrualRates
): { earnedMonths: number; el: number; cl: number; sl: number } {
  return grantsFromRates(computeEarnedMonths(dateOfJoining, year, dateOfExit), rates);
}

/**
 * Days accrued per EARNED MONTH, per code.
 *
 * A rate, not an annual figure. A Leave Policy carries a number and an accrual frequency —
 * "Casual Leave: 1" means one a month on a MONTHLY policy and one for the whole year on a
 * YEARLY one — and LeavePolicyService converts both to this single unit before anything
 * here sees them. One unit below this line is what stops the same number meaning two
 * different things depending on which screen wrote it.
 */
export interface LeaveAccrualRates {
  el: number;
  cl: number;
  sl: number;
}

/**
 * Earned months × the rate, per code.
 *
 * CL and SL round to whole days. EL does NOT: Earned Leave has always kept the half month
 * an employee joining after the 15th earns, and the spec scenarios pin 4.5 and 9.5.
 */
export function grantsFromRates(
  earnedMonths: number,
  rates: LeaveAccrualRates
): { earnedMonths: number; el: number; cl: number; sl: number } {
  return {
    earnedMonths,
    el: earnedMonths * rates.el,
    cl: Math.round(earnedMonths * rates.cl),
    sl: Math.round(earnedMonths * rates.sl),
  };
}
